// 引用情報表示コンポーネント
import React, { useState } from 'react';
import { Citation } from '../types/ApiTypes';
import MarkdownRenderer from './MarkdownRenderer';

interface CitationDisplayProps {
  citations: Citation[];
  className?: string;
}

interface FormattedCitation {
  text: string;
  sources: Array<{
    url: string | null;
    content: string;
    metadata: Record<string, any>;
  }>;
}

// contentからURLを抽出する関数
const extractUrl = (content: string): string | null => {
  const match = content.match(/^--- 元URL: (.+?) 取得日時:/);
  return match ? match[1] : null;
};

// contentからURL部分を除去する関数
const removeUrlPrefix = (content: string): string => {
  return content.replace(/^--- 元URL: .+? ---\s*/, '');
};

// 引用情報をフォーマットする関数
const formatCitations = (citations: Citation[]): FormattedCitation[] => {
  return citations.map(citation => ({
    text: citation.generated_response_part.textResponsePart.text,
    sources: citation.retrieved_references.map(ref => ({
      url: extractUrl(ref.content),
      content: removeUrlPrefix(ref.content),
      metadata: ref.metadata
    }))
  }));
};

// 引用情報表示コンポーネント
export const CitationDisplay: React.FC<CitationDisplayProps> = ({
  citations,
  className = ''
}) => {
  const [expandedCitations, setExpandedCitations] = useState<Set<number>>(new Set());

  const formattedCitations = formatCitations(citations);

  // 引用の展開/折りたたみを切り替え
  const toggleCitation = (index: number) => {
    const newExpanded = new Set(expandedCitations);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCitations(newExpanded);
  };

  if (citations.length === 0) {
    return null;
  }

  return (
    <div className={`citation-display ${className}`}>
      <div className="citation-header">
        <h4>📚 参考情報 ({citations.length}件)</h4>
      </div>

      <div className="citation-list">
        {formattedCitations.map((citation, index) => (
          <div key={index} className="citation-item">
            <div
              className="citation-summary"
              onClick={() => toggleCitation(index)}
              style={{ cursor: 'pointer' }}
            >
              <span className="citation-number">引用 {index + 1}</span>
              <span className="citation-toggle">
                {expandedCitations.has(index) ? '▼' : '▶'}
              </span>
            </div>

            {expandedCitations.has(index) && (
              <div className="citation-sources">
                {citation.sources.map((source, sourceIndex) => (
                  <div key={sourceIndex} className="citation-source">
                    {source.url && (
                      <div className="source-url">
                        <strong>🔗 参照元:</strong>{' '}
                        <a href={source.url} target="_blank" rel="noopener noreferrer">
                          {source.url}
                        </a>
                      </div>
                    )}
                    <div className="source-header">
                      <strong>参照ドキュメント {sourceIndex + 1}:</strong>
                      {source.metadata.title && (
                        <span className="source-name">({source.metadata.title})</span>
                      )}
                    </div>
                    <div className="source-content">
                      <MarkdownRenderer content={source.content} className="citation-markdown" />
                    </div>
                    {Object.keys(source.metadata).length > 0 && (
                      <div className="source-metadata">
                        <details>
                          <summary>メタデータ</summary>
                          <pre>{JSON.stringify(source.metadata, null, 2)}</pre>
                        </details>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// シンプルな引用サマリーコンポーネント
export const CitationSummary: React.FC<{ citations: Citation[] }> = ({ citations }) => {
  if (citations.length === 0) {
    return null;
  }

  return (
    <div className="citation-summary-simple">
      <span className="citation-count">📚 {citations.length}件の参考情報</span>
    </div>
  );
};


